import { ParsedUrlQuery } from 'querystring'
import { TextField } from '@mui/material'
import { SearchInput } from 'components/core/search-input'
import { NextRouter, useRouter } from 'next/router'
import React, { ComponentProps, useEffect, useRef, useState } from 'react'

// TODO(morgabra) centralize these so they don't conflict
export const queryQueryParam = 'query'

type queryFilterProps = {
    query: string
    setQuery: React.Dispatch<React.SetStateAction<string>>
    onChange: (query: string, loading: boolean) => void
    autoFocus?: boolean
} & Omit<ComponentProps<typeof TextField>, 'onChange' | 'onSubmit'>

// TODO(kans): this function should only take `onChange` and `initialState` - query, setQuery should be internal
export const QueryFilter = ({ query, setQuery, onChange, autoFocus, ...rest }: queryFilterProps): React.ReactElement => {
    const router = useRouter()
    const [localQuery, setLocalQuery] = useState<string>('')
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const [loaded, setLoaded] = useState<boolean>(false)

    // Load initial state from query.
    useEffect(() => {
        if (!router.isReady) {
            return
        }
        if (loaded) {
            return
        }
        setLoaded(true)
        let queryStr = router.query[queryQueryParam]
        if (!queryStr) {
            onChange('', true)
            return
        }

        if (Array.isArray(queryStr)) {
            if (queryStr.length === 0) {
                onChange('', true)
                return
            }

            // TODO(morgabra) this doesn't feel super great
            queryStr = queryStr[0]
        }

        queryStr = decodeURIComponent(queryStr)
        setQuery(queryStr)
        onChange(queryStr, true)
    }, [loaded, router, router.query, onChange, setQuery])

    useEffect(() => {
        // set a local instance of query everytime query changes
        setLocalQuery(query)
    }, [query])

    useEffect((): (() => void) => {
        return (): void => {
            if (timer.current) {
                clearTimeout(timer.current)
            }
        }
    }, [])

    // Serialize query string to query param
    const toQuery = (parsedQuery: ParsedUrlQuery, queryStr: string): void => {
        if (queryStr === '') {
            delete parsedQuery[queryQueryParam]
            return
        }
        parsedQuery[queryQueryParam] = encodeURIComponent(queryStr)
    }

    const handleChange = (value: string): void => {
        setLocalQuery(value)
        resetTimer()
        timer.current = setTimeout(() => {
            setQuery(value)
            toQuery(router.query, value)
            onChange(value, false)
        }, 1500)
    }

    const onSubmit = (): void => {
        resetTimer()
        toQuery(router.query, localQuery)
        onChange(localQuery, false)
    }

    const resetTimer = (): void => {
        if (timer.current) {
            clearTimeout(timer.current)
            timer.current = null
        }
    }

    return <SearchInput value={localQuery} onChange={handleChange} onSubmit={onSubmit} autoFocus={autoFocus} {...rest} />
}

type liveQueryFilterProps = {
    onChange: (query: string, loading: boolean) => void
    query: string
    autoFocus?: boolean
}

export const LiveQueryFilter = ({ onChange, query, autoFocus }: liveQueryFilterProps): React.ReactElement => {
    const handleChange = (value: string): void => {
        onChange(value, false)
    }

    const onSubmit = (): void => {
        // TODO @gowthami fix bug where submit retrieves all entitlements
    }

    return <SearchInput value={query} onChange={handleChange} onSubmit={onSubmit} autoFocus={autoFocus} />
}

export const isFiltersSet = ({
    filterQueryParams,
    router,
}: {
    filterQueryParams: string[]
    router: NextRouter
}): boolean => {
    // return true if any router query parameter for each taskFilterQueryParams contain something other than undefined
    return filterQueryParams.map((p) => router.query[p]).filter((q) => q !== undefined).length > 0
}
